import { createError, useNuxtApp } from '#app'
import { useQuery } from '@tanstack/vue-query'
import { provideAnalyticsOrganizationFragment } from '~/composables/use-analytics-script'
import { provideLanguageOrganizationFragment } from '~/composables/use-language'
import { provideLocalizationOrganizationFragment } from '~/composables/use-localization'
import { provideLoginModalOrganizationFragment } from '~/composables/use-login-modal-organization'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import type { OrganizationContextQueryQuery } from '~/resources/graphql/graphql'
import { useI18n } from 'vue-i18n'
import { shallowRef } from 'vue'

type OrganizationContextOrganization = NonNullable<
    OrganizationContextQueryQuery['organization']
>

export async function useOrganizationContext() {
    const nuxtApp = useNuxtApp()
    const { locale } = useI18n({ useScope: 'global' })
    const requestEvent = nuxtApp.ssrContext?.event
    const organization = shallowRef<OrganizationContextOrganization | null>(
        null
    )
    const query = useRpQuery({ orgLevel: true })

    // Provide the organization context ref synchronously during setup, then
    // populate it after the query resolves so consumers keep normal inject semantics.
    provideAnalyticsOrganizationFragment(organization)
    provideLanguageOrganizationFragment(organization, locale, requestEvent)
    provideLocalizationOrganizationFragment(organization)
    provideLoginModalOrganizationFragment(organization)

    const { data, suspense } = useQuery({
        queryKey: ['OrganizationContextQuery'],
        queryFn: () =>
            query(
                graphql(/* GraphQL */ `
                    query OrganizationContextQuery {
                        organization {
                            ...UseAnalyticsScriptOrganizationFragment
                            ...UseLanguageOrganizationFragment
                            ...UseLocalizationOrganizationFragment
                            ...UseLoginModalOrganizationFragment
                        }
                    }
                `)
            )
    })

    await suspense()

    nuxtApp.runWithContext(() => {
        if (data.value?.organization === undefined) {
            throw createError({
                statusCode: 404,
                // Organization context is required before layouts run because layout
                // queries choose translated storefront data from the active locale.
                statusMessage: 'Page Not Found'
            })
        }

        // Fill the provided ref after the query resolves; the language watcher
        // uses synchronous flush so layout storefront queries see the corrected locale.
        organization.value = data.value.organization
    })
}
